import { createContext, useContext, useEffect, useRef } from 'react'

import type { ReactiveVar } from '@apollo/client'
import { makeVar, useReactiveVar } from '@apollo/client'
import get from 'lodash/get'
import set from 'lodash/set'

import type { Path } from '../types/Path'
import { applyFlagOverrides, calculateFlags, readFlagOverrides } from './flags.helpers'
import type { FlagsWithOptionalNetwork } from './flags.types'
import { getFlags } from './flags.types'

let cachedFeatureFlagVar: ReactiveVar<FlagsWithOptionalNetwork> | undefined

// Seeded on first use rather than at module scope: the seed depends on the flags the
// deployment enables globally, which the host application injects after this module's
// import graph has been evaluated.
const featureFlagVar = () => {
  if (!cachedFeatureFlagVar) {
    cachedFeatureFlagVar = makeVar<FlagsWithOptionalNetwork>(applyFlagOverrides(getFlags(), readFlagOverrides()))
  }

  return cachedFeatureFlagVar
}

export const resetFlags = (organisationData?: Maybe<Organisation>, currentUser?: Maybe<User>) => {
  const calculatedFlags = calculateFlags(organisationData, currentUser)

  featureFlagVar()(calculatedFlags)
}

export const useInitialiseFlags = (initialFlags: FlagsWithOptionalNetwork) => {
  const isInitialised = useRef(false)

  useEffect(() => {
    if (!isInitialised.current) {
      setFlags(initialFlags)
      isInitialised.current = true
    }
  }, [initialFlags])
}

export const useUpdateFlag = (key: Path<FlagsWithOptionalNetwork>, value: boolean) => {
  const { ...currentFlagsCopy } = useReactiveVar(featureFlagVar())

  const newFlags = set(currentFlagsCopy, key, value)

  featureFlagVar()(newFlags)
}

export const useFlag = (key: Path<FlagsWithOptionalNetwork>, defaultValue: boolean) => {
  const override = useContext(FlagOverrideContext)
  const globalFlags = useReactiveVar(featureFlagVar())
  const currentFlags = override ?? globalFlags

  return get(currentFlags, key, defaultValue) as boolean
}

export const getFlag = (key: Path<FlagsWithOptionalNetwork>, defaultValue: boolean) =>
  get(featureFlagVar()(), key, defaultValue) as boolean

export const setFlag = (
  currentFlags: FlagsWithOptionalNetwork,
  key: Path<FlagsWithOptionalNetwork>,
  value: boolean,
) => {
  const updatedFlags = { ...currentFlags }

  const newFlags = set(updatedFlags, key, value)

  featureFlagVar()(newFlags)
}

export const setFlags = (flags: FlagsWithOptionalNetwork) => {
  featureFlagVar()(flags)
}

export const useFlags = () => {
  const override = useContext(FlagOverrideContext)
  const globalFlags = useReactiveVar(featureFlagVar())

  return override ?? globalFlags
}

export const useFlagMatch = (
  keyPaths: Path<FlagsWithOptionalNetwork>[],
  defaultValue: boolean,
  requireAll?: boolean,
) => {
  const override = useContext(FlagOverrideContext)
  const globalFlags = useReactiveVar(featureFlagVar())
  const currentFlags = override ?? globalFlags

  const value = keyPaths[requireAll ? 'every' : 'some']((keyPath) => get(currentFlags, keyPath))

  return value ?? defaultValue
}

export const Flag = ({
  keyPath,
  defaultValue,
  fallback,
  children,
}: {
  keyPath: Path<FlagsWithOptionalNetwork>
  defaultValue: boolean
  fallback?: React.ReactNode
  children: React.ReactNode
}) => {
  const flagValue = useFlag(keyPath, defaultValue)

  if (!flagValue) {
    return <>{fallback ?? null}</>
  }

  return <>{children}</>
}

export const FlagProvider = ({ value, children }: { value: FlagsWithOptionalNetwork; children: React.ReactNode }) => {
  useInitialiseFlags(value)

  return <>{children}</>
}

export const FlagOverrideContext = createContext<FlagsWithOptionalNetwork | null>(null)

export const FlagOverrideProvider = ({
  value,
  children,
}: {
  value: FlagsWithOptionalNetwork | null
  children: React.ReactNode
}) => <FlagOverrideContext.Provider value={value}>{children}</FlagOverrideContext.Provider>
